import {
  constructCmsLocaleDict,
  LocaleDict,
  supportedLocales,
  SupportedLocale,
} from "@/i18n";
import type { CmsPageTextColorMode } from "@/lib/cms/cms-theme.constants";

const JSON_CONTENT_FIELDS = [
  "title",
  "content",
  "honorDeclaration",
  "certificationLegalStatement",
  "sidebarTitle",
] as const;

const LOCALE_SUFFIX: Record<SupportedLocale, string> = {
  fr: "FR",
  en: "EN",
  es: "ES",
  nl: "NL",
};

export type ArtworkAuthenticationCmsDto = {
  title?: Partial<Record<SupportedLocale, string>>;
  content?: Partial<Record<SupportedLocale, string>>;
  honorDeclaration?: Partial<Record<SupportedLocale, string>>;
  certificationLegalStatement?: Partial<Record<SupportedLocale, string>>;
  sidebarTitle?: Partial<Record<SupportedLocale, string>>;
  published?: Partial<Record<SupportedLocale, boolean>>;
  pageBackgroundColorId?: string | null;
  pageTextColorMode?: CmsPageTextColorMode;
  pageTextColorId?: string | null;
  pageFontFamily?: string | null;
  pageFontVariant?: string | null;
  homePageFileUrl?: string | null;
  sideBarFileUrl?: string | null;
  /** Legacy flat fields (optional) */
  [key: string]: unknown;
};

export type ArtworkAuthenticationLocaleForm = {
  published: boolean;
  pageName: string;
  pageBackgroundColorId: string | null;
  pageTextColorMode: CmsPageTextColorMode;
  pageTextColorId: string | null;
  pageFontFamily: string | null;
  pageFontVariant: string | null;
  title: string;
  content: string;
  honorDeclaration: string;
  certificationLegalStatement: string;
  sidebarTitle: string;
};

function isLocaleRecord(
  value: unknown,
): value is Partial<Record<SupportedLocale, unknown>> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function pickLocaleString(
  map: Partial<Record<SupportedLocale, unknown>> | undefined,
  locale: SupportedLocale,
): string {
  const v = map?.[locale];
  return typeof v === "string" ? v : "";
}

function pickLocaleBool(
  map: Partial<Record<SupportedLocale, unknown>> | undefined,
  locale: SupportedLocale,
): boolean {
  const v = map?.[locale];
  return typeof v === "boolean" ? v : false;
}

function usesJsonShape(data: ArtworkAuthenticationCmsDto): boolean {
  return JSON_CONTENT_FIELDS.some((field) => isLocaleRecord(data[field]));
}

function apiResponseFromJson(
  data: ArtworkAuthenticationCmsDto,
  websiteLanguages: readonly SupportedLocale[],
): Record<SupportedLocale, ArtworkAuthenticationLocaleForm> {
  const theme = {
    pageBackgroundColorId: data.pageBackgroundColorId ?? null,
    pageTextColorMode: (data.pageTextColorMode ?? "AUTO") as CmsPageTextColorMode,
    pageTextColorId: data.pageTextColorId ?? null,
    pageFontFamily: data.pageFontFamily ?? null,
    pageFontVariant: data.pageFontVariant ?? null,
  };

  return constructCmsLocaleDict((lang) => {
    const title = pickLocaleString(data.title, lang);
    return {
      published: pickLocaleBool(data.published, lang),
      pageName: title || "Artwork Authentication",
      ...theme,
      title,
      content: pickLocaleString(data.content, lang),
      honorDeclaration: pickLocaleString(data.honorDeclaration, lang),
      certificationLegalStatement: pickLocaleString(
        data.certificationLegalStatement,
        lang,
      ),
      sidebarTitle: pickLocaleString(data.sidebarTitle, lang),
    };
  }, websiteLanguages) as Record<SupportedLocale, ArtworkAuthenticationLocaleForm>;
}

/** Legacy flat API: titleFR, contentEN, pageBackgroundColorIdFR, … */
function apiResponseFromFlat(
  data: ArtworkAuthenticationCmsDto,
  websiteLanguages: readonly SupportedLocale[],
): Record<SupportedLocale, ArtworkAuthenticationLocaleForm> {
  const datas = constructCmsLocaleDict(
    () =>
      ({
        published: false,
        pageName: "Artwork Authentication",
        pageBackgroundColorId: null,
        pageTextColorMode: "AUTO" as CmsPageTextColorMode,
        pageTextColorId: null,
        pageFontFamily: null,
        pageFontVariant: null,
        title: "",
        content: "",
        honorDeclaration: "",
        certificationLegalStatement: "",
        sidebarTitle: "",
      }) satisfies ArtworkAuthenticationLocaleForm,
    websiteLanguages,
  ) as Record<SupportedLocale, ArtworkAuthenticationLocaleForm>;

  const sharedThemeKeys = [
    "pageBackgroundColorId",
    "pageTextColorMode",
    "pageTextColorId",
    "pageFontFamily",
    "pageFontVariant",
  ] as const;

  for (const [key, value] of Object.entries(data)) {
    if (key === "homePageFileUrl" || key === "sideBarFileUrl") continue;

    if (sharedThemeKeys.includes(key as (typeof sharedThemeKeys)[number])) {
      for (const lang of websiteLanguages) {
        (datas[lang] as Record<string, unknown>)[key] = value;
      }
      continue;
    }

    const suffix = (key as string).slice(-2).toUpperCase();
    const locale = suffix.toLowerCase() as SupportedLocale;
    if (!supportedLocales.includes(locale)) continue;

    const fieldKey = (key as string).slice(0, -2);
    if (!datas[locale]) continue;
    (datas[locale] as Record<string, unknown>)[fieldKey] = value;
  }

  for (const lang of websiteLanguages) {
    const row = datas[lang];
    if (row.title && !row.pageName) {
      row.pageName = row.title;
    }
  }

  return datas;
}

export function apiResponseToFormLocales(
  data: ArtworkAuthenticationCmsDto | undefined,
  websiteLanguages: readonly SupportedLocale[],
): Record<SupportedLocale, ArtworkAuthenticationLocaleForm> | null {
  if (!data) return null;
  if (usesJsonShape(data)) {
    return apiResponseFromJson(data, websiteLanguages);
  }
  return apiResponseFromFlat(data, websiteLanguages);
}

function emptyLocaleRecord<T>(fill: T): Record<SupportedLocale, T> {
  return supportedLocales.reduce(
    (acc, loc) => ({ ...acc, [loc]: fill }),
    {} as Record<SupportedLocale, T>,
  );
}

export function buildArtworkAuthenticationFormData(
  submittedData: LocaleDict<Record<string, unknown> | undefined>,
  websiteLanguages: readonly SupportedLocale[],
  options: {
    defaultWebsiteLanguage: SupportedLocale;
    homePageFile?: File | null;
    sidebarFile?: File | null;
    cmsId?: string;
  },
): FormData {
  const formData = new FormData();

  if (options.cmsId) {
    formData.append("cmsId", options.cmsId);
  }

  for (const field of JSON_CONTENT_FIELDS) {
    const map = emptyLocaleRecord("");
    for (const lang of websiteLanguages) {
      const localeData = submittedData[lang];
      if (localeData && field in localeData) {
        map[lang] = String((localeData as Record<string, unknown>)[field] ?? "");
      }
    }
    formData.append(field, JSON.stringify(map));
  }

  const publishedMap = emptyLocaleRecord(false);
  for (const lang of websiteLanguages) {
    const published = submittedData[lang]?.published;
    publishedMap[lang] = published === true;
  }
  formData.append("published", JSON.stringify(publishedMap));

  const refTheme = submittedData[options.defaultWebsiteLanguage];
  if (refTheme?.pageBackgroundColorId !== undefined) {
    formData.append(
      "pageBackgroundColorId",
      refTheme.pageBackgroundColorId === null
        ? "null"
        : String(refTheme.pageBackgroundColorId),
    );
  }
  if (refTheme?.pageTextColorMode !== undefined) {
    formData.append("pageTextColorMode", String(refTheme.pageTextColorMode));
  }
  if (refTheme?.pageTextColorId !== undefined) {
    formData.append(
      "pageTextColorId",
      refTheme.pageTextColorId === null ? "null" : String(refTheme.pageTextColorId),
    );
  }
  if (refTheme?.pageFontFamily !== undefined) {
    formData.append(
      "pageFontFamily",
      refTheme.pageFontFamily === null ? "null" : String(refTheme.pageFontFamily),
    );
  }
  if (refTheme?.pageFontVariant !== undefined) {
    formData.append(
      "pageFontVariant",
      refTheme.pageFontVariant === null ? "null" : String(refTheme.pageFontVariant),
    );
  }

  if (options.homePageFile instanceof File) {
    formData.append("homePageFile", options.homePageFile);
  }
  if (options.sidebarFile instanceof File) {
    formData.append("sideBarFile", options.sidebarFile);
  }

  return formData;
}

export function pickArtworkAuthenticationTitle(
  data: ArtworkAuthenticationCmsDto | undefined,
  locale: SupportedLocale,
): string {
  if (!data) return "";
  if (usesJsonShape(data)) {
    return pickLocaleString(data.title, locale);
  }
  const legacyKey = `title${LOCALE_SUFFIX[locale]}`;
  const v = data[legacyKey];
  return typeof v === "string" ? v : "";
}
